import React, { useMemo } from 'react'
import Button from '../../components/Button/Button'
import WorldCupTeamFlag from './WorldCupTeamFlag'
import { ColoredScore, ExactHitBadge, getGuessHits, hasMatchResult } from './WorldCupGuessScore'
import { worldCupStageLabel } from './worldCupTeamFlags'

function fmtGuessCardDate(iso) {
  return new Intl.DateTimeFormat('da-DK', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

function matchStatusLabel(status) {
  if (status === 'live') return 'Live'
  if (status === 'finished') return 'Afsluttet'
  return 'Kommende'
}

export default function WorldCupMyGuessesView({
  myGuesses,
  matches,
  flagCodeByName,
  jokerUsed,
  onGoToMatches,
}) {
  const totalPoints = useMemo(
    () => myGuesses.reduce((sum, g) => sum + (g.pointsAwarded || 0), 0),
    [myGuesses]
  )

  const guessItems = useMemo(
    () =>
      myGuesses
        .map((guess) => ({
          guess,
          match: matches.find((m) => m.id === guess.matchId),
        }))
        .filter((item) => item.match)
        .sort(
          (a, b) =>
            new Date(a.match.kickoffTime).getTime() - new Date(b.match.kickoffTime).getTime()
        ),
    [myGuesses, matches]
  )

  return (
    <div className="worldcup-my-guesses">
      <div className="worldcup-panel-header">
        <h3>Mine VM-gæt</h3>
      </div>

      <div className="worldcup-my-guesses-stats">
        <div className="worldcup-my-guesses-stat">
          <span className="worldcup-my-guesses-stat-label">Gæt i alt</span>
          <strong className="worldcup-my-guesses-stat-value">{myGuesses.length}</strong>
        </div>
        <div className="worldcup-my-guesses-stat worldcup-my-guesses-stat--points">
          <span className="worldcup-my-guesses-stat-label">Point i alt</span>
          <strong className="worldcup-my-guesses-stat-value">{totalPoints}</strong>
        </div>
        <div className="worldcup-my-guesses-stat worldcup-my-guesses-stat--jokers">
          <span className="worldcup-my-guesses-stat-label">Jokere brugt</span>
          <div className="worldcup-my-guesses-joker-row">
            <div className="worldcup-joker-dots" aria-hidden="true">
              {[0, 1, 2].map((slot) => (
                <span
                  key={slot}
                  className={`worldcup-joker-dot${slot < jokerUsed ? ' worldcup-joker-dot--used' : ''}`}
                />
              ))}
            </div>
            <strong className="worldcup-my-guesses-stat-value">{jokerUsed}/3</strong>
          </div>
        </div>
      </div>

      {guessItems.length === 0 ? (
        <div className="worldcup-my-guesses-empty">
          <p className="worldcup-my-guesses-empty-title">Ingen gæt endnu</p>
          <p className="worldcup-my-guesses-empty-text">
            Gå til kampe-fanen og sæt dit første tip, før kampene starter.
          </p>
          {onGoToMatches ? (
            <Button title="Gå til kampe" onClick={onGoToMatches} />
          ) : null}
        </div>
      ) : (
        <ul className="worldcup-my-guesses-list">
          {guessItems.map(({ guess, match }) => {
            const hasResult = hasMatchResult(match)
            const hits = getGuessHits(guess, match)
            const statusClass =
              match.status === 'live' ? 'live' : match.status === 'finished' ? 'finished' : 'scheduled'

            return (
              <li
                key={guess.id}
                className={`worldcup-my-guess-card${
                  hasResult && guess.pointsAwarded > 0
                    ? ` worldcup-my-guess-card--earned${hits?.exactHit ? ' worldcup-my-guess-card--perfect' : ''}`
                    : ''
                }${hasResult && guess.pointsAwarded === 0 ? ' worldcup-my-guess-card--miss' : ''}`}
              >
                <div className="worldcup-my-guess-card-top">
                  <div className="worldcup-my-guess-card-headline">
                    <span className="worldcup-my-guess-date">{fmtGuessCardDate(match.kickoffTime)}</span>
                    <div className="worldcup-my-guess-card-meta">
                      {hits?.exactHit ? <ExactHitBadge /> : null}
                      {hasResult ? (
                        <span
                          className={`worldcup-my-guess-points${
                            guess.pointsAwarded > 0
                              ? ' worldcup-my-guess-points--earned'
                              : ' worldcup-my-guess-points--zero'
                          }`}
                        >
                          {guess.pointsAwarded} pt
                        </span>
                      ) : (
                        <span className="worldcup-my-guess-points worldcup-my-guess-points--pending">
                          Afventer
                        </span>
                      )}
                      {guess.isJoker ? <span className="worldcup-my-guess-joker">Joker</span> : null}
                    </div>
                  </div>
                  <div className="worldcup-my-guess-badges">
                    <span className="worldcup-my-guess-stage">{worldCupStageLabel(match.stage)}</span>
                    <span className={`worldcup-status ${statusClass}`}>
                      {matchStatusLabel(match.status)}
                    </span>
                  </div>
                </div>

                <div className="worldcup-my-guess-fixture">
                  <div className="worldcup-my-guess-teams-bar">
                    <div className="worldcup-my-guess-team worldcup-my-guess-team--home">
                      <WorldCupTeamFlag
                        name={match.homeTeam}
                        flagCode={flagCodeByName[match.homeTeam]}
                        className="worldcup-match-flag worldcup-my-guess-flag"
                      />
                      <span className="worldcup-my-guess-team-name">{match.homeTeam}</span>
                    </div>
                    <span className="worldcup-my-guess-vs" aria-hidden="true">
                      vs
                    </span>
                    <div className="worldcup-my-guess-team worldcup-my-guess-team--away">
                      <WorldCupTeamFlag
                        name={match.awayTeam}
                        flagCode={flagCodeByName[match.awayTeam]}
                        className="worldcup-match-flag worldcup-my-guess-flag"
                      />
                      <span className="worldcup-my-guess-team-name">{match.awayTeam}</span>
                    </div>
                  </div>

                  <div
                    className={`worldcup-my-guess-scores-panel${
                      hasResult ? '' : ' worldcup-my-guess-scores-panel--guess-only'
                    }`}
                  >
                    <ColoredScore
                      label="Dit gæt"
                      home={guess.homeGoals}
                      away={guess.awayGoals}
                      hits={hits}
                    />
                    {hasResult ? (
                      <ColoredScore
                        label="Resultat"
                        home={match.homeGoals}
                        away={match.awayGoals}
                        hits={hits}
                      />
                    ) : null}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
